import { useState, useEffect } from 'react';

/**
 * Hook to delay showing "Access Denied" message to prevent flash during auth loading
 *
 * @param shouldDeny - Boolean condition indicating access should be denied
 * @param isLoading - Boolean indicating if auth/permission check is still loading
 * @param delayMs - Milliseconds to wait before showing denial (default: 1000ms)
 * @returns Object with showDenied boolean and isInGracePeriod boolean
 *
 * @example
 * const { showDenied, isInGracePeriod } = useDelayedAccessDenied(!isPlatformAdmin, loading, 1000);
 *
 * if (isInGracePeriod) {
 *   return <LoadingSpinner />;
 * }
 *
 * if (showDenied) {
 *   return <AccessDeniedMessage />;
 * }
 */
export function useDelayedAccessDenied(
  shouldDeny: boolean,
  isLoading: boolean,
  delayMs: number = 1000
): { showDenied: boolean; isInGracePeriod: boolean } {
  const [showDenied, setShowDenied] = useState(false);
  const [isInGracePeriod, setIsInGracePeriod] = useState(true);

  useEffect(() => {
    // Reset states when loading changes
    if (isLoading) {
      setShowDenied(false);
      setIsInGracePeriod(true);
      return;
    }

    // If not loading and should deny, start delay timer
    if (shouldDeny && !isLoading) {
      setIsInGracePeriod(true);

      const timer = setTimeout(() => {
        setShowDenied(true);
        setIsInGracePeriod(false);
      }, delayMs);

      return () => clearTimeout(timer);
    } else {
      // If should not deny, clear everything immediately
      setShowDenied(false);
      setIsInGracePeriod(false);
    }
  }, [shouldDeny, isLoading, delayMs]);

  return { showDenied, isInGracePeriod };
}
